import React, { useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import 'katex/dist/katex.min.css'
import TypingIndicator from './TypingIndicator'
import LoadingShimmer from './LoadingShimmer'
import {
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  Paper,
  Box,
  Typography,
  Fade,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material'
import {
  FiArrowUp,
  FiArrowDown,
  FiSearch,
  FiPaperclip,
  FiImage,
  FiCopy,
  FiCheck,
  FiRefreshCw,
  FiCpu,
  FiMic,
  FiVolume2,
  FiDownload,
} from 'react-icons/fi'
import CloseIcon from '@mui/icons-material/Close'
import {
  processGroundingCitations,
  GroundingBadge,
  SearchSources,
  SearchSuggestions,
  SearchQueriesDisplay,
} from './GroundingComponents'
import { useAuthContext } from '../../contexts/AuthContext'
import { useChatStore } from '../../stores/useChatStore'
import { useScrollManagement } from '../../hooks/useScrollManagement'
import { useChatActions } from '../../hooks/useChatActions'
import { useShallow } from 'zustand/react/shallow'
import {
  IconWrapper,
  MessageBubble,
  MessageRow,
  MessagesContainer,
} from './MessageList.styles'

const StableVisualImage = React.memo(({ src, alt, hint, caption, onPreview, onRegenerate }) => {
  const theme = useTheme()
  const [loadFailed, setLoadFailed] = useState(false)

  const sizeStyles = {
    small: { width: '100%', maxWidth: 260, mx: 'auto', my: 1.5 },
    medium: { width: '100%', maxWidth: 460, mx: 'auto', display: 'block', my: 2 },
    large: { width: '100%', maxWidth: 620, mx: 'auto', my: 2.5 },
  }
  const layout = sizeStyles[hint] || sizeStyles.medium

  if (loadFailed) {
    return (
      <Box
        sx={{
          ...layout,
          borderRadius: 2,
          p: 2,
          border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
          backgroundColor: alpha(theme.palette.warning.main, 0.06),
          textAlign: 'center',
        }}
      >
        <FiImage size={28} style={{ color: theme.palette.warning.main, opacity: 0.6, marginBottom: 6 }} />
        <Typography variant="caption" sx={{ display: 'block', color: theme.palette.warning.dark, fontWeight: 600, mb: 0.5 }}>
          Immagine scaduta o eliminata
        </Typography>
        {caption && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', fontSize: '0.7rem', mb: 1 }}>
            {caption}
          </Typography>
        )}
        <Box
          component="button"
          onClick={e => { e.stopPropagation(); onRegenerate() }}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
            borderRadius: 1.5,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main, fontSize: '0.75rem', fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s ease',
            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15), borderColor: theme.palette.primary.main },
          }}
        >
          <FiRefreshCw size={12} />
          Rigenera visuali
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ ...layout, overflow: 'hidden', cursor: 'zoom-in' }} onClick={() => onPreview({ src, caption })}>
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={() => setLoadFailed(true)}
        sx={{
          width: '100%',
          aspectRatio: hint === 'small' ? '1 / 1' : hint === 'medium' ? '16 / 10' : '16 / 9',
          objectFit: 'cover', display: 'block',
          borderRadius: hint === 'small' ? 1.5 : 2,
          boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
          animation: 'visualReveal 420ms ease-out',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 10px 28px rgba(0,0,0,0.14)' },
        }}
      />
      <Box sx={{ textAlign: 'center', mt: 0.5 }}>
        {caption && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', fontSize: '0.72rem' }}>
            {caption}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.62rem' }}>
          Clicca per ingrandire
        </Typography>
      </Box>
    </Box>
  )
})

const MessageList = React.memo(() => {
  const theme = useTheme()
  const { user } = useAuthContext()
  const userMessageAnchorRef = useRef(null)
  const assistantResponseRef = useRef(null)
  const spacerRef = useRef(null)
  const messagesEndRef = useRef(null)
  const [previewVisual, setPreviewVisual] = useState(null)
  const { handleCopyMessage, handleRegenerate, handleGenerateVisualExplanation } =
    useChatActions()

  const {
    messagesContainerRef,
    userHasScrolledUp,
    showGoToTop,
    scrollToTop,
    scrollToBottom,
  } = useScrollManagement()

  const {
    selectedConversation,
    messages,
    isLoadingMessages,
    isSendingMessage,
    currentThinkingStatus,
    copiedMessageId,
    regeneratingMessageId,
    streamingMessageId,
    chatElapsedSeconds,
  } = useChatStore(
    useShallow(state => ({
      selectedConversation: state.selectedConversation,
      messages: state.messages,
      isLoadingMessages: state.isLoadingMessages,
      isSendingMessage: state.isSendingMessage,
      currentThinkingStatus: state.currentThinkingStatus,
      copiedMessageId: state.copiedMessageId,
      regeneratingMessageId: state.regeneratingMessageId,
      streamingMessageId: state.streamingMessageId,
      chatElapsedSeconds: state.chatElapsedSeconds,
    }))
  )

  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
      }}
    >
      <Box
        component="img"
        src="/franca-animation-no-bg.gif"
        alt="F.R.A.N.C.A."
        sx={{ mb: 4 }}
      />
      {selectedConversation ? (
        <>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 2,
              fontSize: '1.75rem',
            }}
          >
            Conversazione Iniziata
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 450,
              lineHeight: 1.7,
              fontSize: '1rem',
            }}
          >
            Questo è l'inizio della tua chat "{selectedConversation.title}".
            <br />
            Chiedi qualsiasi cosa o inizia salutando!
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: 'var(--brand-500)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              fontSize: '1.75rem',
            }}
          >
            Benvenuto, {user?.first_name || 'Utente'}!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 450,
              lineHeight: 1.7,
              fontSize: '1rem',
            }}
          >
            Seleziona una conversazione dal pannello di sinistra o iniziane una nuova
            scrivendo qui sotto.
          </Typography>
        </>
      )}
    </Box>
  )

  /**
   * Renders file summary information with professional styling
   */
  const renderFileSummary = fileSummary => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1.5,
        backgroundColor: alpha(theme.palette.info.main, 0.06),
        border: `1.5px solid ${alpha(theme.palette.info.main, 0.2)}`,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2,
          background: alpha(theme.palette.info.main, 0.15),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FiPaperclip size={16} style={{ color: theme.palette.info.main }} />
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: theme.palette.info.main,
            display: 'block',
            mb: 0.5,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          File Elaborati
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            fontSize: '0.875rem',
          }}
        >
          {fileSummary.split('\n').map((line, index) => (
            <span key={index}>
              {line}
              <br />
            </span>
          ))}
        </Typography>
      </Box>
    </Paper>
  )

  /**
   * Renders message content with enhanced markdown styling and grounding citations
   */
  const renderMessageContent = message => {
    const isStreamingMessage = streamingMessageId === message.message_id
    const hasValidContent =
      typeof message.content === 'string' &&
      !/startxref[\s\S]*%%EOF/.test(message.content)

    if (hasValidContent || isStreamingMessage) {
      // Process content with grounding citations if available
      const contentToRender = message.metadata?.grounding_chunks?.length
        ? processGroundingCitations(message.content, message.metadata)
        : message.content
      const visualItems = Array.isArray(message.metadata?.visual_explanation?.items)
        ? message.metadata.visual_explanation.items
        : []
      const visualStatus = message.metadata?.visual_explanation?.status
      const totalVisuals = visualItems.length
      const readyVisuals = visualItems.filter(v => v?.status === 'ready').length
      const loadingVisuals = visualItems.filter(v => v?.status === 'loading').length
      const showVisualProgressBlock =
        message.role === 'assistant' && totalVisuals > 0 && visualStatus === 'loading'
      const shouldRenderInlineVisuals =
        message.role === 'assistant' &&
        visualItems.length > 0 &&
        typeof contentToRender === 'string'

      const markdownComponents = {
        a: ({ node, ...props }) => {
          const linkText = node.children[0]?.value || props.href
          const href = props.href

          // Check if this is a public static file (generated exports/images)
          if (
            href &&
            (href.includes('/generated_exports/') || href.includes('/generated_images/'))
          ) {
            // Convert relative URL to absolute to prevent React Router interception
            const API_BASE =
              process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
            const baseUrl = API_BASE.replace('/api/v1', '')
            const absoluteUrl = href.startsWith('http') ? href : `${baseUrl}${href}`
            const filename = href.split('/').pop()

            return (
              <a
                {...props}
                href={absoluteUrl}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                aria-label={`Download: ${linkText}`}
              >
                <FiDownload size={12} />
                {props.children}
              </a>
            )
          }

          return (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Link a: ${linkText}`}
            >
              {props.children}
            </a>
          )
        },
      }

      const sizeStyles = {
        small: {
          width: '100%',
          maxWidth: 260,
          mx: 'auto',
          my: 1.5,
        },
        medium: {
          width: '100%',
          maxWidth: 460,
          mx: 'auto',
          display: 'block',
          my: 2,
        },
        large: {
          width: '100%',
          maxWidth: 620,
          mx: 'auto',
          my: 2.5,
        },
      }

      const renderInlineVisual = (item, index) => {
        if (!item) return null
        const imageSrc = item.image_url
          ? item.image_url
          : item.image_b64
          ? `data:image/png;base64,${item.image_b64}`
          : null
        const hint = item.size_hint || 'medium'
        const layout = sizeStyles[hint] || sizeStyles.medium
        const stableKey = item.image_url || `vis-${message.message_id}-${index}`

        if (item.status === 'loading') {
          return (
            <Box
              key={`vis-load-${message.message_id}-${index}`}
              sx={{
                ...layout,
                borderRadius: 2,
                p: 1,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.25)}`,
                backgroundColor: alpha(theme.palette.grey[100], 0.5),
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: hint === 'small' ? '1 / 1' : '16 / 10',
                  borderRadius: 1.5,
                  background: `linear-gradient(110deg, ${alpha(
                    theme.palette.grey[300],
                    0.35
                  )} 8%, ${alpha(theme.palette.grey[100], 0.65)} 18%, ${alpha(
                    theme.palette.grey[300],
                    0.35
                  )} 33%)`,
                  backgroundSize: '200% 100%',
                  animation: 'visualShimmer 1.35s linear infinite',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Visual in arrivo (~10s)...
                </Typography>
              </Box>
            </Box>
          )
        }

        if (item.status === 'error') {
          return (
            <Typography
              key={`vis-err-${message.message_id}-${index}`}
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', my: 1, fontStyle: 'italic' }}
            >
              {item.error_message || 'Visuale non disponibile.'}
            </Typography>
          )
        }

        if (item.status === 'expired' || (!imageSrc && item.status === 'ready')) {
          return (
            <Box
              key={`vis-missing-${message.message_id}-${index}`}
              sx={{
                ...layout,
                borderRadius: 2,
                p: 2,
                border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
                backgroundColor: alpha(theme.palette.warning.main, 0.06),
                textAlign: 'center',
              }}
            >
              <FiImage
                size={28}
                style={{
                  color: theme.palette.warning.main,
                  opacity: 0.6,
                  marginBottom: 6,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: theme.palette.warning.dark,
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Immagine non disponibile
              </Typography>
              <Box
                component="button"
                onClick={e => {
                  e.stopPropagation()
                  handleGenerateVisualExplanation(message)
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  borderRadius: 1.5,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <FiRefreshCw size={12} />
                Rigenera visuali
              </Box>
            </Box>
          )
        }

        return (
          <StableVisualImage
            key={stableKey}
            src={imageSrc}
            alt={item.caption || ''}
            hint={hint}
            caption={item.caption}
            onPreview={setPreviewVisual}
            onRegenerate={() => handleGenerateVisualExplanation(message)}
          />
        )
      }

      return (
        <>
          <Box
            sx={{
              overflowY: isStreamingMessage ? 'hidden' : 'visible',
              '@keyframes visualReveal': {
                from: { opacity: 0, transform: 'scale(0.985) translateY(6px)' },
                to: { opacity: 1, transform: 'scale(1) translateY(0)' },
              },
              '@keyframes visualShimmer': {
                to: { backgroundPositionX: '-200%' },
              },
              '& p': {
                lineHeight: 2,
                margin: '0 0 14px 0',
                '&:last-child': { margin: 0 },
              },
              '& ul, & ol': {
                paddingLeft: 24,
                margin: '12px 0',
              },
              '& li': {
                marginBottom: 6,
                lineHeight: 1.6,
              },
              '& code': {
                backgroundColor: alpha(theme.palette.grey[500], 0.15),
                padding: '3px 8px',
                borderRadius: 6,
                fontSize: '0.88em',
                fontFamily:
                  "'JetBrains Mono', 'Fira Code', Monaco, Consolas, monospace",
                fontWeight: 500,
              },
              '& pre': {
                padding: 20,
                borderRadius: 12,
                overflow: 'auto',
                margin: '16px 0',
                border: `1px solid ${alpha(theme.palette.grey[300], 0.3)}`,
                backgroundColor: alpha(theme.palette.grey[50], 0.5),
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              },
              '& blockquote': {
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                paddingLeft: 16,
                margin: '12px 0',
                fontStyle: 'italic',
                color: theme.palette.text.secondary,
              },
              '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex, rehypeRaw]}
              components={markdownComponents}
            >
              {!shouldRenderInlineVisuals
                ? contentToRender + (isStreamingMessage ? '▍' : '')
                : ''}
            </ReactMarkdown>

            {shouldRenderInlineVisuals &&
              (() => {
                const lines = contentToRender.split('\n')
                const sections = []
                let buf = []
                let headingLabel = '_intro'
                for (const line of lines) {
                  const hm = line.match(/^#{1,4}\s+(.+)/)
                  if (hm && buf.length > 0) {
                    sections.push({ key: headingLabel, text: buf.join('\n').trim() })
                    buf = [line]
                    headingLabel = hm[1].trim().toLowerCase().replace(/\s+/g, '-').slice(0, 40)
                  } else {
                    if (hm) headingLabel = hm[1].trim().toLowerCase().replace(/\s+/g, '-').slice(0, 40)
                    buf.push(line)
                  }
                }
                if (buf.length) sections.push({ key: headingLabel, text: buf.join('\n').trim() })
                const textSections = sections.filter(s => s.text)

                const visualBySectionIdx = {}
                visualItems.forEach((v, i) => {
                  const key = v.sectionIndex != null ? v.sectionIndex : i
                  visualBySectionIdx[key] = v
                })

                return textSections.map((section, sIdx) => {
                  const matchedVisual = visualBySectionIdx[sIdx]

                  return (
                    <Box
                      key={`sec-${message.message_id}-${section.key}-${sIdx}`}
                      sx={{
                        overflow: 'hidden',
                        py: 0.5,
                        mb: 1.5,
                        borderBottom:
                          sIdx < textSections.length - 1
                            ? `1px solid ${alpha(theme.palette.grey[300], 0.35)}`
                            : 'none',
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkMath]}
                        rehypePlugins={[rehypeKatex, rehypeRaw]}
                        components={markdownComponents}
                      >
                        {section.text + (isStreamingMessage && sIdx === textSections.length - 1 ? '▍' : '')}
                      </ReactMarkdown>
                      {matchedVisual && renderInlineVisual(matchedVisual, sIdx)}
                    </Box>
                  )
                })
              })()}
          </Box>

          {showVisualProgressBlock && (
            <Box
              sx={{
                mt: 1.25,
                mb: 0.5,
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                backgroundColor: alpha(theme.palette.info.main, 0.06),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.6,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.info.dark,
                    letterSpacing: '0.02em',
                  }}
                >
                  Sto generando visuali per le sezioni sopra
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
                >
                  {readyVisuals}/{totalVisuals}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={totalVisuals > 0 ? (readyVisuals / totalVisuals) * 100 : 0}
                sx={{
                  height: 6,
                  borderRadius: 99,
                  mb: 0.8,
                  backgroundColor: alpha(theme.palette.info.main, 0.15),
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: theme.palette.text.secondary,
                  fontSize: '0.72rem',
                }}
              >
                Le immagini appariranno automaticamente nei punti rilevanti della risposta.
                {loadingVisuals > 0 ? ` In lavorazione: ${loadingVisuals}.` : ''}
              </Typography>
            </Box>
          )}

          {/* Display search suggestions if available */}
          {!isStreamingMessage && message.metadata && (
            <SearchSuggestions metadata={message.metadata} />
          )}

          {/* Display search queries used */}
          {!isStreamingMessage && message.metadata && (
            <SearchQueriesDisplay metadata={message.metadata} />
          )}

          {/* Display search sources at the bottom */}
          {!isStreamingMessage && message.metadata && (
            <SearchSources metadata={message.metadata} />
          )}
        </>
      )
    }

    return (
      <Typography
        variant="body2"
        sx={{
          fontStyle: 'italic',
          color: theme.palette.text.secondary,
        }}
      >
        Contenuto binario o non visualizzabile caricato.
      </Typography>
    )
  }

  /**
   * Renders tool usage indicators with enhanced styling
   */
  const renderToolIndicators = toolInteractions => {
    const webSearchTool = toolInteractions?.find(tool => tool.name === 'search_web')

    if (webSearchTool) {
      return (
        <Tooltip title="Ricerca web utilizzata" placement="top">
          <Chip
            icon={<FiSearch size={12} />}
            label="Web"
            size="small"
            variant="outlined"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              borderColor: alpha(theme.palette.success.main, 0.3),
              color: theme.palette.success.dark,
              '& .MuiChip-icon': {
                fontSize: '12px',
                color: theme.palette.success.main,
              },
            }}
          />
        </Tooltip>
      )
    }

    return null
  }

  /**
   * Renders export download button for generated exports
   */
  // eslint-disable-next-line no-unused-vars
  const renderExportDownload = toolInteractions => {
    // Look for export generation tool
    const exportTool = toolInteractions?.find(
      tool =>
        tool.name === 'generate_conversation_export' ||
        tool.name === 'export_conversation' ||
        tool.name === 'generate_export'
    )

    if (!exportTool) {
      return null
    }

    // Handle loading state while export is being generated
    if (exportTool.status === 'running' || exportTool.status === 'pending') {
      return (
        <Box
          className="export-download-box loading"
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: alpha(theme.palette.info.main, 0.06),
            border: `1.5px dashed ${alpha(theme.palette.info.main, 0.3)}`,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            className="export-icon-container"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: alpha(theme.palette.info.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CircularProgress
              size={20}
              thickness={4}
              className="export-loading-spinner"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: theme.palette.info.main,
                display: 'block',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
              }}
            >
              Generazione Export in Corso
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              Attendere prego...
            </Typography>
          </Box>
        </Box>
      )
    }

    // Handle error state
    if (exportTool.status === 'error' || exportTool.status === 'failed') {
      return (
        <Box
          className="export-download-box export-error-box"
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.06),
            border: `1.5px solid ${alpha(theme.palette.error.main, 0.2)}`,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            className="export-icon-container"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: alpha(theme.palette.error.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              flexShrink: 0,
            }}
          >
            ⚠️
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: theme.palette.error.main,
                display: 'block',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
              }}
            >
              Errore Generazione Export
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              {exportTool.error_message ||
                'Si è verificato un errore durante la generazione del file.'}
            </Typography>
          </Box>
        </Box>
      )
    }

    // Handle successful export with download link
    if (!exportTool.output_summary) {
      return null
    }

    try {
      let result

      // Try to parse output_summary if it's a string
      if (typeof exportTool.output_summary === 'string') {
        result = JSON.parse(exportTool.output_summary)
      } else {
        result = exportTool.output_summary
      }

      if (!result.download_url || !result.filename) {
        console.warn('Export tool result missing download_url or filename:', result)
        return null
      }

      // Convert relative URL to absolute URL to prevent React Router interception
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
      // Remove /api/v1 from base URL if present, as static files are served from root
      const baseUrl = API_BASE.replace('/api/v1', '')
      const absoluteDownloadUrl = result.download_url.startsWith('http')
        ? result.download_url
        : `${baseUrl}${result.download_url}`

      // Determine format and emoji
      const formatLabel = (
        result.export_format ||
        result.format ||
        'file'
      ).toUpperCase()
      const getFormatEmoji = format => {
        const fmt = (format || '').toLowerCase()
        switch (fmt) {
          case 'pdf':
            return '📄'
          case 'txt':
          case 'text':
            return '📝'
          case 'docx':
          case 'doc':
            return '📘'
          case 'xlsx':
          case 'xls':
          case 'csv':
            return '📊'
          case 'zip':
          case 'rar':
            return '📦'
          case 'json':
            return '📋'
          default:
            return '📎'
        }
      }
      const formatEmoji = getFormatEmoji(result.export_format || result.format)

      // Get file size if available
      const fileSize = result.file_size || result.size
      const formatFileSize = bytes => {
        if (!bytes) return ''
        if (bytes < 1024) return `${bytes} B`
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      }

      return (
        <Box
          className="export-download-box export-success-box"
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: alpha(theme.palette.success.main, 0.06),
            border: `1.5px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: alpha(theme.palette.success.main, 0.1),
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows[2],
            },
          }}
        >
          <Box
            className="export-icon-container"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: alpha(theme.palette.success.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              flexShrink: 0,
            }}
          >
            {formatEmoji}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: theme.palette.success.dark,
                display: 'block',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
              }}
            >
              Export Generato
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontSize: '0.875rem',
                fontWeight: 600,
                mb: 0.25,
              }}
            >
              {result.filename}
            </Typography>
            {fileSize && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                {formatFileSize(fileSize)}
              </Typography>
            )}
          </Box>
          <Box
            component="a"
            href={absoluteDownloadUrl}
            download={result.filename}
            target="_blank"
            rel="noopener noreferrer"
            className="export-download-button"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2.5,
              py: 1.25,
              backgroundColor: theme.palette.success.main,
              color: 'white',
              borderRadius: 2,
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              transition: 'all 0.2s ease-in-out',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: theme.palette.success.dark,
                transform: 'translateY(-1px)',
                boxShadow: theme.shadows[4],
              },
            }}
          >
            <FiDownload size={16} />
            Scarica {formatLabel}
          </Box>
        </Box>
      )
    } catch (error) {
      console.error('Error parsing export tool result:', error, exportTool)

      // Show error state
      return (
        <Box
          className="export-download-box export-error-box"
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.06),
            border: `1.5px solid ${alpha(theme.palette.error.main, 0.2)}`,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            className="export-icon-container"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: alpha(theme.palette.error.main, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              flexShrink: 0,
            }}
          >
            ⚠️
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: theme.palette.error.main,
                display: 'block',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.5,
              }}
            >
              Errore Formato Export
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
              }}
            >
              Impossibile analizzare i dati del file esportato.
            </Typography>
          </Box>
        </Box>
      )
    }
  }

  /**
   * Renders message action buttons with professional styling
   */
  const renderMessageActions = message => {
    const isUser = message.role === 'user'

    const isValidContent =
      typeof message.content === 'string' && message.content.length > 0
    const isStreamingMessage = streamingMessageId === message.message_id
    const isLastAssistantMessage =
      message.role === 'assistant' &&
      messages[messages.length - 1].message_id === message.message_id
    const canRegenerate =
      isLastAssistantMessage && !isSendingMessage && !streamingMessageId
    const canGenerateVisual =
      !isUser && !isStreamingMessage && !isSendingMessage && message.content?.trim()
    const visualStatus = message.metadata?.visual_explanation?.status
    const isVisualLoading = visualStatus === 'loading'

    if (!isValidContent) return null

    return (
      <Box
        className="message-actions"
        sx={{
          position: 'absolute',
          top: 24,
          left: isUser && -10,
          right: !isUser && -10,
          borderRadius: '8px',
          display: 'flex',
          gap: 0.5,
          opacity: 0,
          backgroundColor: theme.palette.neutral[200],
          pointerEvents: 'none',
          transform: 'translateY(2px)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
        }}
      >
        {/* COPY */}
        <Tooltip title="Copia Messaggio" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={() => handleCopyMessage(message.content, message.message_id)}
              disabled={isStreamingMessage}
              sx={{
                color: theme.palette.primary[800],
                pointerEvents: 'auto',
              }}
            >
              {copiedMessageId === message.message_id ? (
                <FiCheck color={theme.palette.success.main} size={16} />
              ) : (
                <FiCopy size={16} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {/* REGENERATE */}
        {canRegenerate && (
          <Tooltip title="Rigenera risposta" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => handleRegenerate(message)}
                disabled={
                  !!regeneratingMessageId || streamingMessageId || isSendingMessage
                }
                sx={{
                  color: theme.palette.primary[800],
                  pointerEvents: 'auto',
                }}
              >
                {regeneratingMessageId === message.message_id ? (
                  <CircularProgress size={14} />
                ) : (
                  <FiRefreshCw size={14} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* VISUALIZE */}
        {canGenerateVisual && (
          <Tooltip title="Visualizza spiegazione" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => handleGenerateVisualExplanation(message)}
                disabled={isVisualLoading}
                sx={{
                  color: theme.palette.primary[800],
                  pointerEvents: 'auto',
                }}
              >
                {isVisualLoading ? <CircularProgress size={14} /> : <FiImage size={14} />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    )
  }

  /**
   * Determines the appropriate ref for a message
   */
  const getMessageRef = (message, index) => {
    if (message.role === 'assistant' && streamingMessageId === message.message_id) {
      return assistantResponseRef
    }

    if (message.role === 'user' && index === messages.length - 1) {
      return userMessageAnchorRef
    }

    return null
  }

  /**
   * Renders individual message bubble with professional styling
   */
  const renderMessage = (message, index) => {
    const isUser = message.role === 'user'
    const isAudioTranscription = message.source_type === 'audio_transcription'
    const isAudioResponse = message.source_type === 'audio_response'

    return (
      <Fade in timeout={300} key={message.message_id || `msg-${index}`}>
        <MessageRow ref={getMessageRef(message, index)} isUser={isUser}>
          {!isUser && (
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'transparent',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="F.R.A.N.C.A."
                sx={{ width: '100%', height: '100%' }}
              />
            </Avatar>
          )}

          <MessageBubble isUser={isUser} variant={message.source_type}>
            <Box sx={{ paddingX: 1 }}>
              <IconWrapper>
                {isAudioTranscription && (
                  <FiMic
                    size={14}
                    color={
                      isUser ? 'rgba(255,255,255,0.9)' : theme.palette.primary.main
                    }
                    style={{ opacity: 0.8 }}
                  />
                )}
                {isAudioResponse && (
                  <FiVolume2
                    size={14}
                    color={
                      isUser ? 'rgba(255,255,255,0.9)' : theme.palette.primary.main
                    }
                    style={{ opacity: 0.8 }}
                  />
                )}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isUser ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.85)',
                    fontSize: 12,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {isAudioResponse && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ml: 0.5,
                        fontStyle: 'italic',
                        opacity: 0.7,
                        fontSize: 10,
                        textTransform: 'lowercase',
                      }}
                    >
                      (pronunciato)
                    </Typography>
                  )}
                </Typography>

                {message.role === 'assistant' &&
                  renderToolIndicators(message.toolInteractions)}
                {/* Display grounding badge for assistant messages with grounding data */}
                {message.role === 'assistant' && message.metadata && (
                  <GroundingBadge metadata={message.metadata} />
                )}
              </IconWrapper>

              {/* File Summary for user messages */}
              {message.role === 'user' &&
                message.fileSummary &&
                renderFileSummary(message.fileSummary)}

              <Box sx={{ marginBottom: 0, fontSize: 14, lineHeight: 1.7 }}>
                {renderMessageContent(message)}
              </Box>

            </Box>

            {renderMessageActions(message)}
          </MessageBubble>
        </MessageRow>
      </Fade>
    )
  }

  /**
   * Renders enhanced scroll control buttons
   */
  const renderScrollControls = () => (
    <>
      {showGoToTop && (
        <Tooltip title="Vai in cima" placement="left">
          <Fade in>
            <IconButton
              onClick={() => scrollToTop()}
              sx={{
                position: 'fixed',
                bottom: 80,
                right: 24,
                backgroundColor: 'background.paper',
                boxShadow: theme.shadows[8],
                zIndex: 1000,
              }}
            >
              <FiArrowUp size={18} color="#64748b" />
            </IconButton>
          </Fade>
        </Tooltip>
      )}

      {userHasScrolledUp && (
        <Tooltip title="Vai in fondo" placement="left">
          <Fade in>
            <IconButton
              onClick={() => scrollToBottom('smooth')}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                background: 'var(--brand-500)',
                color: 'white',
                boxShadow: theme.shadows[8],
                zIndex: 1000,
                '&:hover': { background: 'var(--brand-600)' },
              }}
            >
              <FiArrowDown size={18} />
            </IconButton>
          </Fade>
        </Tooltip>
      )}
    </>
  )

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {selectedConversation && selectedConversation.active_arcana_id && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            background: 'var(--brand-500)',
            color: 'white',
            fontWeight: 'bold',
            zIndex: 1,
          }}
        >
          <FiCpu size={20} />
          <Typography variant="subtitle1" fontWeight="bold" color="inherit">
            Chiacchierata Arcano :
            {selectedConversation.active_arcana_id || 'Arcano Selezionato'}
          </Typography>
        </Box>
      )}
      <MessagesContainer ref={messagesContainerRef}>
        {isLoadingMessages && <LoadingShimmer />}

        {!isLoadingMessages && messages.length === 0
          ? renderEmptyState()
          : messages.map(renderMessage)}

        {isSendingMessage && currentThinkingStatus && (
          <TypingIndicator
            status={currentThinkingStatus}
            elapsedSeconds={chatElapsedSeconds}
          />
        )}

        <Box ref={spacerRef} />
        <Box ref={messagesEndRef} />
      </MessagesContainer>

      {renderScrollControls()}

      <Dialog
        open={Boolean(previewVisual)}
        onClose={() => setPreviewVisual(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: '#0b1020',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => setPreviewVisual(null)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 2,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.35)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {previewVisual && (
            <Box
              component="img"
              src={previewVisual.src}
              alt={previewVisual.caption || 'Visual preview'}
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            justifyContent: 'space-between',
            background:
              'linear-gradient(180deg, rgba(16,22,44,0.95) 0%, rgba(8,12,28,0.98) 100%)',
            color: 'rgba(255,255,255,0.92)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              maxWidth: '75%',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.78rem',
              letterSpacing: '0.01em',
            }}
          >
            {previewVisual?.caption || 'Visuale esplicativa'}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}
          >
            Clic fuori o ESC per chiudere
          </Typography>
        </DialogActions>
      </Dialog>
    </Box>
  )
})

export default MessageList
